import { useEffect, useState } from 'react';
import {
  resolveVisualViewport,
  type VisualViewport,
} from './visualViewport';

/**
 * Classifies the current viewport for graph/grid surfaces.
 * - phone:  ≤767px
 * - tablet: 768–1023px
 * - desktop: ≥1024px
 */
export function useVisualViewport(): VisualViewport {
  const [viewport, setViewport] = useState<VisualViewport>(() => {
    if (typeof window === 'undefined') return 'desktop';
    return resolveVisualViewport(window.innerWidth);
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const update = () => {
      setViewport(resolveVisualViewport(window.innerWidth));
    };

    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  return viewport;
}

/** Convenience flags derived from {@link useVisualViewport}. */
export function useVisualViewportFlags() {
  const viewport = useVisualViewport();
  return {
    viewport,
    isPhone: viewport === 'phone',
    isTablet: viewport === 'tablet',
    isDesktop: viewport === 'desktop',
    /** True when width is tablet or narrower (≤1023px). */
    isCompact: viewport !== 'desktop',
  } as const;
}

export { VISUAL_VIEWPORT_PHONE_MAX, VISUAL_VIEWPORT_TABLET_MAX } from './visualViewport';
